import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const MetricsModal = ({
  isOpen,
  onClose,
  allMetrics,
  initialSelectedMetrics,
  onApply,
  selectionLimit = 5, // New Prop for setting max allowed metrics
}) => {
  const [tempMetrics, setTempMetrics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(["Performance"]);

  // Sync initial state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Ensure we don't exceed the limit on load if the limit changed
      setTempMetrics(initialSelectedMetrics.slice(0, selectionLimit));
      setSearchQuery("");
    }
  }, [isOpen, initialSelectedMetrics, selectionLimit]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || typeof document === "undefined") return null;

  // Group metrics for the modal sidebar
  const groupedMetrics = allMetrics.reduce((acc, metric) => {
    if (metric.visible === false) return acc; // Hide internal metrics
    if (!acc[metric.category]) acc[metric.category] = [];
    acc[metric.category].push(metric);
    return acc;
  }, {});

  const handleCheckboxChange = (key, checked) => {
    if (checked) {
      if (selectionLimit === 1) {
        // Auto-swap if limit is 1 (better UX for Pie charts)
        setTempMetrics([key]);
      } else if (tempMetrics.length < selectionLimit) {
        // Add to array if under limit
        setTempMetrics([...tempMetrics, key]);
      }
    } else {
      // Uncheck
      setTempMetrics(tempMetrics.filter((m) => m !== key));
    }
  };

  const handleApply = () => {
    onApply(tempMetrics);
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex justify-end">
      {/* Blacken background overlay */}
      <div
        className="absolute inset-0 bg-black/60 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Drawer Panel */}
      <div className="relative w-full md:w-[75vw] lg:w-[65vw] max-w-5xl h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center px-6 py-4 border-b border-gray-200 shrink-0 bg-white">
          <button
            onClick={onClose}
            className="mr-4 text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-xl font-normal text-gray-800">Modify metrics</h2>
        </div>

        {/* Modal Body split pane */}
        <div className="flex flex-1 overflow-hidden bg-white">
          {/* Left Settings Pane */}
          <div className="flex-1 flex flex-col border-r border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 shrink-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Select metrics to add to the graph"
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg
                  className="absolute left-3.5 top-3 text-gray-400"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {Object.entries(groupedMetrics).map(([category, metrics]) => {
                const filteredMetrics = metrics.filter((m) =>
                  m.title.toLowerCase().includes(searchQuery.toLowerCase()),
                );
                if (searchQuery && filteredMetrics.length === 0) return null;
                const isExpanded =
                  expandedCategories.includes(category) || searchQuery !== "";

                return (
                  <div key={category} className="border-b border-gray-100">
                    <button
                      onClick={() =>
                        setExpandedCategories((prev) =>
                          isExpanded
                            ? prev.filter((c) => c !== category)
                            : [...prev, category],
                        )
                      }
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 focus:outline-none"
                    >
                      <span className="font-medium text-gray-700 text-sm">
                        {category}
                      </span>
                      <svg
                        className={`text-gray-400 transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6 px-6 pb-5 pt-1">
                        {filteredMetrics.map((m) => {
                          const isChecked = tempMetrics.includes(m.key);
                          const isDisabled =
                            !isChecked &&
                            tempMetrics.length >= selectionLimit &&
                            selectionLimit !== 1;

                          return (
                            <label
                              key={m.key}
                              className={`flex items-center space-x-3 group ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={(e) =>
                                  handleCheckboxChange(m.key, e.target.checked)
                                }
                                className={`w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                              />
                              <span
                                className={`text-sm select-none transition-colors ${isDisabled ? "text-gray-400" : "text-gray-600 group-hover:text-gray-900"}`}
                              >
                                {m.title}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Selection Summary Pane */}
          <div className="w-[30%] min-w-[280px] shrink-0 flex flex-col bg-gray-50/50">
            <div className="px-6 py-4 border-b border-gray-200 shrink-0">
              <h3 className="font-semibold text-gray-800 text-sm">
                Your columns ({tempMetrics.length}/{selectionLimit})
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {selectionLimit === 1
                  ? "Select exactly 1 metric"
                  : "Drag and drop to reorder"}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              {tempMetrics.length === 0 && (
                <div className="text-center text-gray-400 text-sm mt-10">
                  No metrics selected
                </div>
              )}
              {tempMetrics.map((key) => {
                const metric = allMetrics.find((m) => m.key === key);
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between px-3 py-2.5 bg-white border border-gray-200 rounded-md shadow-sm mb-2 group hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center space-x-3 overflow-hidden">
                      {selectionLimit > 1 && (
                        <svg
                          className="text-gray-400 cursor-grab shrink-0 hover:text-gray-600"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M4 8h16M4 16h16" />
                        </svg>
                      )}
                      <span className="text-sm text-gray-700 truncate">
                        {metric?.title || key}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCheckboxChange(key, false)}
                      className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                      title="Remove metric"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center space-x-3 bg-white shrink-0">
          <button
            onClick={handleApply}
            disabled={tempMetrics.length === 0}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            Apply
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-blue-600 bg-transparent text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MetricsModal;
